import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import HelmetWrapper from '../../components/common/HelmetWrapper';
import DeadlyCrossingNav from './DeadlyCrossingNav';

/**
 * Public privacy policy for Deadly Crossing (Google AdMob / App Store / Play).
 */
const DeadlyCrossingPrivacyPage = () => {
  return (
    <PageLayout>
      <HelmetWrapper>
        <title>Deadly Crossing — Privacy Policy</title>
        <meta
          name="description"
          content="Privacy Policy for Deadly Crossing by Zok Labs. Last updated August 2026."
        />
        <meta name="robots" content="noindex,follow" />
      </HelmetWrapper>

      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border p-8">
            <DeadlyCrossingNav />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Deadly Crossing Privacy Policy</h1>
            <p className="text-gray-600 mb-8">
              <strong>Last updated:</strong> August 2026
            </p>

            <div className="prose max-w-none">
              <p className="text-gray-700 mb-8">
                Deadly Crossing (“we”, “the game”) is a mobile game that shows advertisements through
                Google AdMob. Scenes are free to play. A banner may stay at the bottom of the screen;
                rewarded ads may grant power packs or a revive; and interstitials may appear before
                some runs.
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">What we collect</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    Advertising identifiers and device information used by Google AdMob to serve and
                    measure ads (subject to your consent / App Tracking Transparency choice on iOS).
                  </li>
                  <li>
                    Local game progress on your device only (unlocked scenes, power-pack counts, games
                    played). This save file never leaves your phone.
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">What we do not collect</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    We do not create accounts, and we do not ask for your name, email, or precise
                    location.
                  </li>
                  <li>We do not sell your personal information.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your choices</h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>
                    On iOS you can allow or deny tracking via the system App Tracking Transparency
                    prompt.
                  </li>
                  <li>
                    In regions that require it, Google’s User Messaging Platform consent form is shown
                    before personalized ads.
                  </li>
                  <li>You can reset your advertising identifier in your device settings.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact</h2>
                <p className="text-gray-700">
                  If you have questions about this policy or the game, email{' '}
                  <a
                    href="mailto:omerwaqaskhan@gmail.com"
                    className="text-emerald-600 hover:text-emerald-800 hover:underline"
                  >
                    omerwaqaskhan@gmail.com
                  </a>{' '}
                  or visit{' '}
                  <Link
                    to="/deadly-crossing/support"
                    className="text-emerald-600 hover:text-emerald-800 hover:underline"
                  >
                    Support
                  </Link>
                  .
                </p>
              </section>

              <section className="mb-2">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Credits</h2>
                <p className="text-gray-700">
                  Power icons by John Colburn and Lorc. Available on{' '}
                  <a
                    href="https://game-icons.net"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-800 hover:underline"
                  >
                    https://game-icons.net
                  </a>{' '}
                  (CC BY 3.0).
                </p>
              </section>
            </div>

            <p className="mt-10 text-sm text-gray-500">Publisher: Zok Labs</p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default DeadlyCrossingPrivacyPage;
